import type { components } from '@trackercop/contracts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { CrawlProgressStrip } from '@/components/crawl-progress-strip';

type HarnessCoverageReport = components['schemas']['HarnessCoverageReport'];
type HarnessCoverageScenario = components['schemas']['HarnessCoverageScenario'];

type Tone = 'positive' | 'negative' | 'warning' | 'neutral';

export function CoverageReport({ report }: { report: HarnessCoverageReport }) {
  return (
    <div className="flex flex-col gap-4">
      <CrawlProgressStrip crawlId={report.crawlId} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>Harness coverage</span>
            <Badge variant="outline" data-testid="harness-coverage-status">
              {report.status}
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Crawl <code>{report.crawlId}</code> against{' '}
            <code>{report.harnessBaseUrl ?? 'unknown'}</code>
          </p>
        </CardHeader>
        <CardContent>
          <CoverageRollup report={report} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scenarios</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="harness-coverage-table">
            <THead>
              <TR>
                <TH>Scenario</TH>
                <TH>Status</TH>
                <TH>Expected families</TH>
                <TH>Observed</TH>
                <TH>Missing</TH>
                <TH>Events</TH>
              </TR>
            </THead>
            <TBody>
              {report.scenarios.map((s) => (
                <ScenarioRow key={s.scenarioId} scenario={s} />
              ))}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function CoverageRollup({ report }: { report: HarnessCoverageReport }) {
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <Stat label="Total" value={report.overall.totalScenarios} />
      <Stat
        label="Fully covered"
        value={report.overall.fullyCovered}
        tone="positive"
      />
      <Stat
        label="Partial"
        value={report.overall.partial}
        tone={report.overall.partial > 0 ? 'warning' : 'neutral'}
      />
      <Stat
        label="Missing"
        value={report.overall.missing}
        tone={report.overall.missing > 0 ? 'negative' : 'neutral'}
      />
    </div>
  );
}

function ScenarioRow({ scenario }: { scenario: HarnessCoverageScenario }) {
  const statusTone: Tone =
    scenario.status === 'fully-covered'
      ? 'positive'
      : scenario.status === 'partial'
        ? 'warning'
        : 'negative';
  return (
    <TR data-scenario-id={scenario.scenarioId}>
      <TD>
        <code className="text-xs">{scenario.scenarioId}</code>
      </TD>
      <TD>
        <ToneBadge tone={statusTone}>{scenario.status}</ToneBadge>
      </TD>
      <TD>
        <FamilyChips families={scenario.expectedTrackerFamilies} />
      </TD>
      <TD>
        <FamilyChips
          families={scenario.observedTrackerFamilies}
          ghostFamilies={scenario.unexpectedTrackerFamilies}
        />
      </TD>
      <TD>
        <FamilyChips families={scenario.missingTrackerFamilies} tone="negative" />
      </TD>
      <TD>
        <div className="flex flex-col gap-0.5 text-[11px]">
          <span>Observed: {scenario.observedEvents.join(', ') || '—'}</span>
          {scenario.missingExpectedEvents.length > 0 && (
            <span className="text-destructive">
              Missing: {scenario.missingExpectedEvents.join(', ')}
            </span>
          )}
        </div>
      </TD>
    </TR>
  );
}

function FamilyChips({
  families,
  ghostFamilies = [],
  tone,
}: {
  families: readonly string[];
  ghostFamilies?: readonly string[];
  tone?: 'negative';
}) {
  if (families.length === 0 && ghostFamilies.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {families.map((f) => (
        <ToneBadge key={f} tone={tone === 'negative' ? 'negative' : 'neutral'}>
          {f}
        </ToneBadge>
      ))}
      {ghostFamilies.map((f) => (
        <ToneBadge key={'ghost-' + f} tone="warning" title="Off-spec — not in expectedTrackerFamilies">
          {f}*
        </ToneBadge>
      ))}
    </div>
  );
}

function ToneBadge({
  children,
  tone = 'neutral',
  title,
}: {
  children: React.ReactNode;
  tone?: Tone;
  title?: string;
}) {
  const styles: Record<Tone, string> = {
    positive: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    negative: 'bg-rose-100 text-rose-800 border-rose-300',
    warning: 'bg-amber-100 text-amber-800 border-amber-300',
    neutral: 'bg-muted text-muted-foreground border-muted',
  };
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] ${styles[tone]}`}
      title={title}
    >
      {children}
    </span>
  );
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  tone?: Tone;
}) {
  const colour =
    tone === 'positive'
      ? 'text-emerald-700'
      : tone === 'negative'
        ? 'text-rose-700'
        : tone === 'warning'
          ? 'text-amber-700'
          : 'text-foreground';
  return (
    <div className="flex flex-col rounded border px-3 py-2">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className={`text-2xl font-semibold ${colour}`}>{value}</span>
    </div>
  );
}
